import { linkifyText } from "@/src/utils/linkify.utils";

// Reduces article HTML to plain text with markdown-lite markers
// (`**bold**`, `*italic*`, `[text](url)`) so it renders as native nested
// `Text`, no HTML parser needed.

export type FormattedSegment = Readonly<{
  text: string;
  bold?: boolean;
  italic?: boolean;
  url?: string | null;
}>;

type Run =
  | { kind: "text"; text: string; bold: boolean; italic: boolean; url: string | null }
  | { kind: "break"; size: 1 | 2 };

// Matches ANY tag generically — not just the ones handled below — so an
// unrecognized tag (Quill's `<span class="ql-ui">` bullet marker, `<ol>`,
// `<img>`, …) is consumed whole and discarded instead of falling through to
// the text branch and leaking its raw markup (a real bug this fixed: an
// unmatched tag left its `<` unconsumed, and the rest of its characters got
// swept up as if they were article text).
const TOKEN_RE = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>|([^<]+)/gi;
const HREF_RE = /href\s*=\s*["']([^"']+)["']/i;
const LIST_TYPE_RE = /data-list\s*=\s*["'](\w+)["']/i;
const BLOCK_CLOSERS = new Set([
  "p", "div", "li", "ol", "ul", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6",
]);
const HEADING_TAGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

const decodeEntities = (value: string): string =>
  value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

const tokenizeHtml = (html: string): Run[] => {
  const runs: Run[] = [];
  let boldDepth = 0;
  let italicDepth = 0;
  let url: string | null = null;
  const urlStack: (string | null)[] = [];
  // Set on `<li>`, consumed by the item's first text run — a bullet/number
  // to prepend since the real marker (browser `list-style`, or Quill's own
  // `::before` on its empty `.ql-ui` span) has nothing to render from here.
  let pendingMarker: string | null = null;
  let orderedIndex = 0;

  const re = new RegExp(TOKEN_RE.source, TOKEN_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(html)) !== null) {
    const [, closing, tag, attrs, text] = match;

    if (text !== undefined) {
      let decoded = decodeEntities(text).replace(/\s+/g, " ");
      if (pendingMarker && decoded) {
        decoded = pendingMarker + decoded;
        pendingMarker = null;
      }
      if (decoded) {
        runs.push({ kind: "text", text: decoded, bold: boldDepth > 0, italic: italicDepth > 0, url });
      }
      continue;
    }

    const name = (tag ?? "").toLowerCase();
    if (name === "br") {
      runs.push({ kind: "break", size: 1 });
    } else if (!closing) {
      if (name === "strong" || name === "b" || HEADING_TAGS.has(name)) boldDepth++;
      else if (name === "em" || name === "i") italicDepth++;
      else if (name === "a") {
        urlStack.push(url);
        url = HREF_RE.exec(attrs ?? "")?.[1] ?? null;
      } else if (name === "ol" || name === "ul") {
        orderedIndex = 0;
      } else if (name === "li") {
        const listType = LIST_TYPE_RE.exec(attrs ?? "")?.[1]?.toLowerCase();
        pendingMarker = listType === "ordered" ? `${++orderedIndex}. ` : "• ";
      }
    } else {
      if (name === "strong" || name === "b" || HEADING_TAGS.has(name)) {
        boldDepth = Math.max(0, boldDepth - 1);
      } else if (name === "em" || name === "i") {
        italicDepth = Math.max(0, italicDepth - 1);
      } else if (name === "a") {
        url = urlStack.pop() ?? null;
      }
      if (BLOCK_CLOSERS.has(name)) runs.push({ kind: "break", size: 2 });
    }
  }

  return runs;
};

const wrapRun = (text: string, bold: boolean, italic: boolean, url: string | null): string => {
  if (url) return `[${text}](${url})`;
  let wrapped = text;
  if (bold) wrapped = `**${wrapped}**`;
  if (italic) wrapped = `*${wrapped}*`;
  return wrapped;
};

// Uncapped by default — the card's own "See more" clamp does the
// truncating at display time, so cutting the stored content short here
// would hide the rest of the article even after expanding.
export const htmlToFormattedExcerpt = (
  html?: string | null,
  maxLength = Infinity,
): string => {
  if (!html) return "";

  const runs = tokenizeHtml(html);
  const parts: string[] = [];
  let visibleLength = 0;
  let truncated = false;
  let pendingBreak: 1 | 2 | null = null;
  let wroteText = false;

  for (const run of runs) {
    if (run.kind === "break") {
      if (wroteText) pendingBreak = pendingBreak === 2 || run.size === 2 ? 2 : run.size;
      continue;
    }
    if (visibleLength >= maxLength) {
      truncated = true;
      break;
    }

    let text = wroteText ? run.text : run.text.replace(/^\s+/, "");
    if (!text) continue;

    const remaining = maxLength - visibleLength;
    if (text.length > remaining) {
      const clipped = text.slice(0, remaining);
      const lastSpace = clipped.lastIndexOf(" ");
      text = (lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).replace(/[.,;:!?-]+$/, "");
      truncated = true;
    }

    if (pendingBreak) {
      parts.push(pendingBreak === 2 ? "\n\n" : "\n");
      pendingBreak = null;
    }

    parts.push(wrapRun(text, run.bold, run.italic, run.url));
    visibleLength += text.length;
    wroteText = true;

    if (truncated) break;
  }

  const result = parts.join("").trim();
  return truncated ? `${result}…` : result;
};

const MARKER_RE =
  /\[([^\]]+)\]\(([^)\s]+)\)|\*\*\*([^*\n]+)\*\*\*|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*/g;

export const parseFormattedSegments = (text?: string | null): FormattedSegment[] => {
  if (!text) return [];

  const segments: FormattedSegment[] = [];
  const pushPlain = (chunk: string) => {
    if (!chunk) return;
    for (const segment of linkifyText(chunk)) {
      segments.push({ text: segment.text, url: segment.url });
    }
  };

  const re = new RegExp(MARKER_RE.source, MARKER_RE.flags);
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > cursor) pushPlain(text.slice(cursor, match.index));

    const [, linkText, linkUrl, boldItalicText, boldText, italicText] = match;
    if (linkText !== undefined) {
      segments.push({ text: linkText, url: linkUrl });
    } else if (boldItalicText !== undefined) {
      segments.push({ text: boldItalicText, bold: true, italic: true });
    } else if (boldText !== undefined) {
      segments.push({ text: boldText, bold: true });
    } else if (italicText !== undefined) {
      segments.push({ text: italicText, italic: true });
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) pushPlain(text.slice(cursor));

  return segments;
};

export const toPlainFormattedText = (text?: string | null): string =>
  parseFormattedSegments(text)
    .map((segment) => segment.text)
    .join("");
